import {Component, HostListener, Inject, OnDestroy, OnInit, Renderer2, ViewEncapsulation} from '@angular/core';
import {CommonModule, DOCUMENT} from '@angular/common';
import {NavigationEnd, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {ConstantsService} from '../../fidelidade/ConstantsService';
import {MonitoradorPedidos} from '../../fidelidade/MonitoradorPedidos';
import {FidelidadeComponent} from '../../fidelidade/fidelidade.component';
import {AutorizacaoService} from '../../services/autorizacao.service';
import {RotaGuardClient} from '../../guards/auth-loja.guard';
import {ServidorImpressaoExecutorService} from '../../services/servidor-impressao-executor.service';
import {FuncaoMenuV2} from '../menu-v2/menu-v2.catalogo';
import {criarCatalogoParaUsuario} from '../menu-v2/menu-v2.contexto';
import {UsoMenuV2} from '../menu-v2/menu-v2.uso';
import {apliqueFaseDoOnboarding} from '../menu-v2/menu-v2.fases';

/** As portas de venda; cada uma só existe se o catálogo a liberar. */
const PORTAS_DE_VENDA = [
  {id: 'pedidos/novo-v3', nome: 'Pedido para entrega ou retirada', detalhe: 'O cliente ligou ou mandou mensagem', icone: 'fe-truck', cor: 'azul'},
  {id: 'pedidos/pdv/novo', nome: 'Venda no balcão', detalhe: 'Cobra e entrega na hora', icone: 'fe-shopping-bag', cor: 'teal'},
  {id: 'pedidos-mesas', nome: 'Pedido de mesa', detalhe: 'Abre ou continua a comanda', icone: 'fe-grid', cor: 'roxo'}
];

/**
 * Telas de tarefa, com barra de ação própria no rodapé: ali o menu some, como
 * nos apps de celular, e volta quando a tarefa termina.
 */
const ROTAS_SEM_BARRA: RegExp[] = [
  /^\/admin\/pedidos\/(novo|novo-v3|novo-mesa|pdv\/novo)(\/|$)/,
  /^\/admin\/pedidos\/detalhes\//,
  /^\/admin\/produtos-v2\/.+/,
  /^\/admin\/adicionais-v2\/.+/,
  /^\/admin\/tabelas-precos(\/|$)/,
  /^\/admin\/(comecar|wizard|wizard-antigo)(\/|$)/
];

const CLASSE_NO_BODY = 'mc-barra-celular-ativa';

/**
 * Menu no rodapé do admin no celular: Início, Pedidos, Vender, Produtos e
 * Funções em todas as telas, menos nas de tarefa. Cada botão só aparece com a
 * permissão ou o módulo dele; "Vender" sai do catálogo do menu-v2, que já filtra
 * por permissão, módulo, recurso e fase do onboarding, e "Funções" abre o menu do ☰.
 */
@Component({
  selector: 'app-barra-celular',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './barra-celular.component.html',
  styleUrls: ['./barra-celular.component.scss'],
  // O espaço no fim do conteúdo é da casca (.content-page); por isso o estilo não é encapsulado.
  encapsulation: ViewEncapsulation.None
})
export class BarraCelularComponent implements OnInit, OnDestroy {
  usuario: any = {};
  empresa: any = null;
  funcoes: FuncaoMenuV2[] = [];
  readonly uso = new UsoMenuV2(UsoMenuV2.armazenamentoLocal());

  temModuloPedidos = false;
  temModuloFidelidade = false;
  podeLancarPontos = false;
  podeOperarCaixa = false;

  rota = '';
  telaEstreita = false;
  folha: '' | 'vender' = '';

  private readonly dentroDaExtensao: boolean;
  private inscricoes: Subscription[] = [];

  constructor(private router: Router, private constants: ConstantsService, private monitorador: MonitoradorPedidos,
              private autorizacao: AutorizacaoService, private servidorImpressao: ServidorImpressaoExecutorService,
              private casca: FidelidadeComponent, private renderer: Renderer2, @Inject(DOCUMENT) private documento: Document) {
    let dentro = true;
    try { dentro = window.self !== window.top; } catch (e) {}
    this.dentroDaExtensao = dentro;
  }

  ngOnInit(): void {
    this.usuario = this.autorizacao.getUser() || {};
    this.podeLancarPontos = RotaGuardClient.adicionarPontosFidelidade(this.usuario);
    this.podeOperarCaixa = RotaGuardClient.operarCaixa(this.usuario);
    this.rota = this.router.url;
    this.aoRedimensionar();

    this.inscricoes.push(this.constants.empresa$.subscribe((empresa: any) => {
      if (!empresa) return;
      this.empresa = empresa;
      this.monteCatalogo();
    }));
    this.inscricoes.push(this.constants.moduloPedido$.subscribe((tem: any) => this.temModuloPedidos = !!tem));
    this.inscricoes.push(this.constants.moduloFidelidade$.subscribe((tem: any) => this.temModuloFidelidade = !!tem));
    this.inscricoes.push(this.router.events.pipe(filter(evento => evento instanceof NavigationEnd)).subscribe((evento: NavigationEnd) => {
      this.rota = evento.urlAfterRedirects || evento.url;
      this.fecheFolha();
      this.marqueBody();
    }));
  }

  ngOnDestroy(): void {
    this.inscricoes.forEach((inscricao: Subscription) => inscricao.unsubscribe());
    this.renderer.removeClass(this.documento.body, CLASSE_NO_BODY);
  }

  @HostListener('window:resize')
  aoRedimensionar(): void {
    this.telaEstreita = typeof window !== 'undefined' && window.matchMedia('(max-width: 767.98px)').matches;
    this.marqueBody();
  }

  @HostListener('document:keydown.escape')
  aoApertarEsc(): void {
    if (this.folha) this.fecheFolha();
  }

  /** Celular, fora da extensão, não garçom e fora das telas de tarefa. */
  get visivel(): boolean {
    if (!this.telaEstreita || this.dentroDaExtensao || this.usuario.garcom || !this.empresa) return false;
    const caminho = this.rota.split('?')[0].split('#')[0];
    return !ROTAS_SEM_BARRA.some(rota => rota.test(caminho));
  }

  /** A casca ganha espaço no fim só quando a barra está na tela. */
  private marqueBody(): void {
    if (this.visivel) this.renderer.addClass(this.documento.body, CLASSE_NO_BODY);
    else this.renderer.removeClass(this.documento.body, CLASSE_NO_BODY);
  }

  private monteCatalogo(): void {
    const catalogo = criarCatalogoParaUsuario(this.usuario, this.empresa, this.constants, this.servidorImpressao.ehUmServidorAtivo());
    this.funcoes = apliqueFaseDoOnboarding(catalogo, (this.empresa && this.empresa.onboarding) || null);
    this.marqueBody();
  }

  // ─────────────────────────── Botões ───────────────────────────

  private get caminho(): string {
    return this.rota.split('?')[0].split('#')[0];
  }

  get naInicio(): boolean {
    return this.caminho === '/admin/index' || this.caminho === '/admin';
  }

  get emPedidos(): boolean {
    return this.caminho.startsWith('/admin/pedidos') && !this.caminho.startsWith('/admin/pedidos-mesas');
  }

  get noCardapio(): boolean {
    return this.caminho.startsWith('/admin/produtos-v2');
  }

  /** Pedidos ainda não vistos: o mesmo número do contador de novos da casca. */
  get novosPedidos(): number {
    return (this.monitorador.novosPedidos || []).length;
  }

  get funcaoCardapio(): FuncaoMenuV2 | undefined {
    return this.funcoes.find((f: FuncaoMenuV2) => f.id === 'produtos-v2');
  }

  /** Mesas no lugar de Produtos para quem não vê vendas, quando o catálogo libera o salão. */
  get funcaoMesas(): FuncaoMenuV2 | undefined {
    if (RotaGuardClient.visualizaRelatorios(this.usuario)) return undefined;
    return this.funcoes.find((f: FuncaoMenuV2) => f.id === 'pedidos-mesas');
  }

  get noSalao(): boolean {
    return this.caminho.startsWith('/admin/pedidos-mesas');
  }

  get portasDeVenda(): Array<{funcao: FuncaoMenuV2, nome: string, detalhe: string, icone: string, cor: string}> {
    return PORTAS_DE_VENDA
      .map(porta => ({...porta, funcao: this.funcoes.find((f: FuncaoMenuV2) => f.id === porta.id)}))
      // O catálogo libera o PDV de balcão pelo recurso da loja; vender no balcão é operar o caixa.
      .filter(porta => !!porta.funcao && (porta.id !== 'pedidos/pdv/novo' || this.podeOperarCaixa));
  }

  get exibirLancarPontos(): boolean {
    return this.temModuloFidelidade && this.podeLancarPontos;
  }

  get exibirVender(): boolean {
    return this.portasDeVenda.length > 0 || this.exibirLancarPontos;
  }

  abraInicio(): void {
    if (this.naInicio) {
      window.scrollTo({top: 0, behavior: 'smooth'});
      return;
    }
    this.router.navigateByUrl('/admin/index').then(() => {});
  }

  abraPedidos(): void {
    this.router.navigateByUrl('/admin/pedidos').then(() => {});
  }

  abraVender(): void {
    this.folha = 'vender';
  }

  lancePontos(): void {
    this.fecheFolha();
    this.router.navigateByUrl('/admin/contatos/pontos/adicionar').then(() => {});
  }

  // ─────────────────────────── Funções ───────────────────────────

  /** O mesmo menu do ☰; sem parar o clique, o "clique fora" do menu o fecharia na hora. */
  abraFuncoes(evento: MouseEvent): void {
    evento.stopPropagation();
    this.fecheFolha();
    this.casca.abraMenu();
  }

  /** Mesmo caminho do menu-v2: registra o uso e respeita ação, rota ou link externo. */
  execute(funcao: FuncaoMenuV2): void {
    this.monteCatalogo();
    const atual = this.funcoes.find((f: FuncaoMenuV2) => f.id === funcao.id);
    if (!atual) return;

    this.uso.registrar(atual.id);
    this.fecheFolha();

    if (atual.acao) {
      if (atual.acao === 'abrirLoja') this.casca.abraModalReabrirLoja();
      else if (atual.acao === 'fecharLoja') this.casca.abraModalFecharLoja();
      else if (atual.acao === 'impressao') this.casca.abraModalGerenciarJobs();
      else if (atual.acao === 'sair') this.casca.logout();
      else this.router.navigateByUrl('/admin/pedidos').then(() => {});
    } else if (atual.rota && !atual.externa) {
      this.router.navigateByUrl(atual.rota).then(() => {});
    } else if (atual.rota) {
      window.open(atual.rota, '_blank', 'noopener,noreferrer');
    }
  }

  fecheFolha(): void {
    this.folha = '';
  }
}
